import React, { useState } from "react";

/**
 * Dus ka Dum bet table in the reference markup (allpanel9 admin, casino/dum10), inside .casino-detail:
 *   .casino-box-row > .casino-nation-name (b nat + (i) range collapse + .float-right books)
 *                   + .casino-bl-box > .back / .lay .casino-bl-box-item[.suspended] > .casino-box-odd
 *   .dkd-other.mt-2 > .casino-box-row (4 × b odds: Even, Odd, Red, Black)
 *                   + .casino-box-row (4 × back item: "Even", "Odd", ♥♦ images, ♠♣ images + book)
 *                   + .text-center.w-100 > span.float-right.casino-min-max "R:min-max"
 * Styled by the ported reference rules (.duskadum … in index.css).
 * main = subtype "dum10" by sr, fancy = subtype "dum10fancy" by sr ([Red, Black, Even, Odd]).
 * Suspended unless gstatus is OPEN. Book: .book-green when > 0, otherwise .book-red.
 */

// v-kvalmin / v-kvalmax: >= 1000 → "10K", >= 100000 → "1L", else the plain number
const kval = (size) => {
  const n = Number(size);
  if (!Number.isFinite(n)) return "";
  if (n >= 1e5) return `${parseFloat((n / 1e5).toFixed(2))}L`;
  if (n >= 1e3) return `${parseFloat((n / 1e3).toFixed(2))}K`;
  return `${parseFloat(n.toFixed(2))}`;
};

// toCurrency (EN-IN, max 2 decimals); 0 stays 0
const toCurrency = (v) => (v ? parseFloat(v).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : 0);

const SUIT = `${import.meta.env.BASE_URL}img/cards`;

export default function BetTableDum10({ main = [], fancy = [], onBetClick, exposures = {} }) {
  const [rangeOpen, setRangeOpen] = useState(false);

  const book = (nat) => {
    if (!nat) return 0;
    const v = exposures[nat] ?? exposures[nat.toLowerCase()] ?? exposures[nat.trim()] ?? 0;
    return parseFloat(v) || 0;
  };
  const bookSpan = (amount) => (
    <span className={`casino-book ${amount > 0 ? "book-green" : "book-red"}`}>{toCurrency(amount)}</span>
  );
  const isOpen = (item) => item?.gstatus === "OPEN";
  const click = (item, value, type) => {
    if (!item || !isOpen(item)) return;
    onBetClick(value, item.nat, type, item);
  };

  // sid 1 carries the whole main market (back `b`, lay `l`); the feed's sid-2 duplicate stays SUSPENDED at 0/0
  const m0 = main.find((s) => Number(s?.sid) === 1) || main[0];
  const layOpen = isOpen(m0) && Number(m0?.l) > 0;
  // padlock pinned to the top only when the cell has a book, so the figure stays readable
  const fancyBack = (item, content) => (
    <div className="casino-bl-box">
      <div
        className={`back casino-bl-box-item${content.img ? " casino-card-img" : ""}${book(item?.nat) ? " lock-top" : ""}${isOpen(item) ? "" : " suspended"}`}
        onClick={() => click(item, item?.b, "back")}
      >
        {content.node} {bookSpan(book(item?.nat))}
      </div>
    </div>
  );

  return (
    <>
      <div className="casino-box-row">
        <div className="casino-nation-name">
          <div className="p-relative">
            <b>{m0?.nat}</b>{" "}
            <div className="d-inline-block">
              <i
                className={rangeOpen ? "fas fa-info-circle" : "fas fa-info-circle collapsed"}
                data-toggle="collapse"
                data-target="#demo"
                aria-expanded={rangeOpen ? "true" : "false"}
                onClick={() => setRangeOpen((v) => !v)}
              />{" "}
              <div id="demo" className={rangeOpen ? "icon-range collapse show" : "icon-range collapse"}>
                R:<span>{kval(m0?.min)}</span>-<span>{kval(m0?.max)}</span>
              </div>
            </div>
          </div>{" "}
          <div className="float-right mr-2">
            {/* server books "<nat>back" = if the total lands, "<nat>lay" = if it doesn't (panelCasinoBook) */}
            <span className="d-block">{bookSpan(book(m0?.nat && `${m0.nat.trim()}back`))}</span>{" "}
            <span className="d-block">{bookSpan(book(m0?.nat && `${m0.nat.trim()}lay`))}</span>
          </div>
        </div>{" "}
        <div className="casino-bl-box">
          <div className={`back casino-bl-box-item${isOpen(m0) ? "" : " suspended"}`} onClick={() => click(m0, m0?.b, "back")}>
            <span className="casino-box-odd">{m0?.b}</span>
          </div>{" "}
          <div className={`lay casino-bl-box-item${layOpen ? "" : " suspended"}`} onClick={() => layOpen && click(m0, m0?.l, "lay")}>
            <span className="casino-box-odd">{m0?.l}</span>
          </div>
        </div>
      </div>{" "}
      <div className="dkd-other mt-2">
        <div className="casino-box-row">
          {[fancy[2], fancy[3], fancy[0], fancy[1]].map((item, i) => (
            <React.Fragment key={i}>
              {i > 0 && " "}
              <div className="casino-bl-box">
                <b>{item?.b}</b>
              </div>
            </React.Fragment>
          ))}
        </div>{" "}
        <div className="casino-box-row">
          {fancyBack(fancy[2], { node: <span className="casino-box-odd">{fancy[2]?.nat}</span> })}{" "}
          {fancyBack(fancy[3], { node: <span className="casino-box-odd">{fancy[3]?.nat}</span> })}{" "}
          {fancyBack(fancy[0], {
            img: true,
            node: (
              <span>
                <img src={`${SUIT}/heart.png`} alt="" /> <img src={`${SUIT}/diamond.png`} alt="" />
              </span>
            ),
          })}{" "}
          {fancyBack(fancy[1], {
            img: true,
            node: (
              <span>
                <img src={`${SUIT}/spade.png`} alt="" /> <img src={`${SUIT}/club.png`} alt="" />
              </span>
            ),
          })}
        </div>{" "}
        <div className="text-center w-100">
          <span className="float-right casino-min-max">
            R:<span>{kval(fancy[0]?.min)}</span>-<span>{kval(fancy[0]?.max)}</span>
          </span>
        </div>
      </div>
    </>
  );
}
